import {
  Activity,
  Dumbbell,
  Flame,
  Gauge,
  MoonStar,
  RotateCcw,
  Snowflake,
  Timer,
} from 'lucide-react';
import { hasDisplayValue } from '../lib/utils';
import StreamCharts from './StreamCharts';

type PlannedWorkoutPlanProps = {
  details?: unknown;
  intervals?: unknown;
};

const parsePlannedIntervals = (intervals: unknown): any[] => {
  if (!hasDisplayValue(intervals)) return [];
  try {
    const parsed =
      typeof intervals === 'string' ? JSON.parse(intervals) : intervals;
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [{ type: 'legacy', notes: String(intervals) }];
  }
};

const sectionAppearance = (interval: any) => {
  const intensity = String(
    interval.intensity || interval.steps?.[0]?.intensity || interval.type || '',
  ).toLowerCase();

  if (intensity.includes('warmup'))
    return { icon: Flame, label: 'Warm-up', color: 'text-amber-400' };
  if (intensity.includes('cooldown'))
    return { icon: Snowflake, label: 'Cool-down', color: 'text-sky-400' };
  if (intensity.includes('recover') || intensity.includes('rest'))
    return { icon: MoonStar, label: 'Recovery', color: 'text-emerald-400' };
  if (interval.type === 'repeat')
    return { icon: RotateCcw, label: 'Repeat set', color: 'text-violet-400' };
  if (intensity.includes('strength'))
    return { icon: Dumbbell, label: 'Strength', color: 'text-lime-400' };
  return { icon: Activity, label: 'Main set', color: 'text-blue-400' };
};

const intervalSectionTitle = (interval: any, index: number) => {
  if (interval.type === 'repeat') return `${interval.repeat || 1}× Repeat`;
  if (interval.type === 'legacy') return 'Workout';
  return interval.name || interval.type || `Section ${index + 1}`;
};

const numericValue = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const formatPace = (value: unknown, unit: unknown) => {
  const normalizedUnit = String(unit || '').toLowerCase();
  if (normalizedUnit === 'minutes_per_mile') return `${String(value)}/mi`;

  const seconds = numericValue(value);
  if (!seconds || !normalizedUnit.includes('seconds_per_')) {
    return String(value ?? '');
  }

  const roundedSeconds = Math.round(seconds);
  const minutes = Math.floor(roundedSeconds / 60);
  const remainingSeconds = roundedSeconds % 60;
  const distanceUnit = normalizedUnit.includes('kilometer') ? 'km' : 'mi';
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}/${distanceUnit}`;
};

const formatUnit = (unit: unknown, value: unknown) => {
  const normalized = String(unit || '').replace(/_/g, ' ');
  if (Number(value) === 1) return normalized.replace(/s$/, '');
  return normalized;
};

const stepFacts = (step: any) => {
  const duration = step.duration
    ? [step.duration.value, formatUnit(step.duration.unit, step.duration.value)]
        .filter(Boolean)
        .join(' ')
    : '';
  const target = step.target
    ? step.target.low && step.target.high
      ? `${formatPace(step.target.low, step.target.unit)}–${formatPace(step.target.high, step.target.unit)}`
      : formatPace(step.target.value, step.target.unit)
    : '';

  return { duration, target };
};

const StepDetails = ({
  step,
  fallbackTitle,
  hideTitle = false,
}: {
  step: any;
  fallbackTitle: string;
  hideTitle?: boolean;
}) => {
  const { duration, target } = stepFacts(step);
  return (
    <div className="rounded-xl border border-border/80 bg-muted/50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {hideTitle ? null : (
          <h5 className="font-bold text-card-foreground">
            {step.name || step.type || fallbackTitle}
          </h5>
        )}
        <div className="flex flex-wrap gap-2">
          {duration ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-xs font-bold text-card-foreground ring-1 ring-border">
              <Timer className="h-3.5 w-3.5" />
              {duration}
            </span>
          ) : null}
          {target ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-xs font-bold text-card-foreground ring-1 ring-border">
              <Gauge className="h-3.5 w-3.5" />
              {target}
            </span>
          ) : null}
          {step.intensity ? (
            <span className="rounded-full bg-brand-blueSoft px-2.5 py-1 text-xs font-bold capitalize text-brand-midnight">
              {step.intensity}
            </span>
          ) : null}
        </div>
      </div>
      {step.notes ? (
        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
          {step.notes}
        </p>
      ) : null}
    </div>
  );
};

const secondsPerMileFromTarget = (target: any): number | null => {
  if (!target || String(target.type).toLowerCase() !== 'pace') return null;
  const unit = String(target.unit || '').toLowerCase();
  const paceValue = (rawValue: unknown) => {
    if (unit !== 'minutes_per_mile') return numericValue(rawValue);
    const match = String(rawValue || '').match(/^(\d+):([0-5]\d)$/);
    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
  };
  const value = paceValue(target.value);
  const low = paceValue(target.low);
  const high = paceValue(target.high);
  const pace = value || (low && high ? (low + high) / 2 : null);
  if (!pace) return null;
  return unit.includes('kilometer') || unit.includes('km')
    ? pace * 1.60934
    : pace;
};

const metersFromDuration = (duration: any): number | null => {
  const value = numericValue(duration?.value);
  if (String(duration?.type).toLowerCase() !== 'distance' || !value)
    return null;
  const unit = String(duration.unit || '').toLowerCase();
  if (unit.includes('mile')) return value * 1609.34;
  if (unit.includes('kilometer') || unit.includes('km')) return value * 1000;
  if (unit.includes('yard')) return value * 0.9144;
  return value;
};

const secondsFromDuration = (duration: any): number | null => {
  const value = numericValue(duration?.value);
  if (String(duration?.type).toLowerCase() !== 'time' || !value) return null;
  return String(duration.unit || '')
    .toLowerCase()
    .includes('minute')
    ? value * 60
    : value;
};

const flattenStructuredIntervals = (intervals: unknown): any[] =>
  parsePlannedIntervals(intervals).flatMap((interval) => {
    if (interval.type === 'repeat' && Array.isArray(interval.steps)) {
      return Array.from({ length: interval.repeat || 1 }).flatMap(
        (_, repeatIndex) =>
          interval.steps.map((step: any) => ({
            ...step,
            name: step.name || `${step.type || 'Step'} ${repeatIndex + 1}`,
          })),
      );
    }
    return [interval];
  });

const plannedChartIntervalsFromWorkout = (intervals: unknown) =>
  flattenStructuredIntervals(intervals)
    .map((step, index) => {
      const targetPace = secondsPerMileFromTarget(step.target);
      const distance = metersFromDuration(step.duration);
      const elapsedTime = secondsFromDuration(step.duration);
      const estimatedDistance =
        !distance && elapsedTime && targetPace
          ? (elapsedTime / targetPace) * 1609.34
          : null;
      const estimatedElapsedTime =
        !elapsedTime && distance && targetPace
          ? (distance / 1609.34) * targetPace
          : null;
      return {
        name: step.name || step.type || `Step ${index + 1}`,
        intensity: step.intensity || step.type,
        elapsedTime: elapsedTime || estimatedElapsedTime || 0,
        distance: distance || estimatedDistance || 0,
        targetPace: targetPace || undefined,
      };
    })
    .filter((interval) => interval.distance > 0);

export default function PlannedWorkoutPlan({
  details,
  intervals,
}: PlannedWorkoutPlanProps) {
  const intervalSections = parsePlannedIntervals(intervals);
  const plannedChartIntervals = plannedChartIntervalsFromWorkout(intervals);

  return (
    <div className="space-y-6">
      {hasDisplayValue(details) ? (
        <section>
          <h3 className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">
            Training notes
          </h3>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-card-foreground">
            {String(details)}
          </p>
        </section>
      ) : null}

      {plannedChartIntervals.length > 0 ? (
        <StreamCharts streams={null} intervals={plannedChartIntervals} />
      ) : null}

      {intervalSections.length > 0 ? (
        <section className="space-y-3">
          <h3 className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">
            Workout sections
          </h3>
          {intervalSections.map((interval, index) => {
            const appearance = sectionAppearance(interval);
            const Icon = appearance.icon;
            return (
              <article
                key={`${interval.type || 'section'}-${index}`}
                className="rounded-2xl border border-border bg-card p-4 text-card-foreground shadow-sm sm:p-5"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-xl bg-muted p-2.5 ${appearance.color}`}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-muted-foreground">
                      {appearance.label}
                    </p>
                    <h4 className="mt-0.5 text-base font-black capitalize text-card-foreground">
                      {intervalSectionTitle(interval, index)}
                    </h4>
                  </div>
                </div>
                {interval.type === 'repeat' ? (
                  Array.isArray(interval.steps) ? (
                    <div className="mt-4 space-y-2">
                      {interval.steps.map((step: any, stepIndex: number) => (
                        <StepDetails
                          key={`${step.type || 'step'}-${stepIndex}`}
                          step={step}
                          fallbackTitle={`Step ${stepIndex + 1}`}
                        />
                      ))}
                    </div>
                  ) : null
                ) : interval.type === 'legacy' ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
                    {interval.notes}
                  </p>
                ) : (
                  <div className="mt-4">
                    <StepDetails
                      step={interval}
                      fallbackTitle="Exercise"
                      hideTitle={
                        intervalSectionTitle(interval, index) ===
                        (interval.name || interval.type)
                      }
                    />
                  </div>
                )}
              </article>
            );
          })}
        </section>
      ) : null}
    </div>
  );
}
